import type { ActivityRow, WireRequest } from '@/api/wire';
import { toolName } from '@/app/technical';
import { actionWords, outcomeWords } from '@/oversight/auditWords';
import type { ItemContext, Party } from '@/ui/context';
import type { Who } from '@/ui/who';
import { scopeWords } from '@/ui/words';

/**
 * What a request says about itself (ADR-0155, §18). Pure: the row the list
 * or the page already holds, and who is reading it.
 *
 * A request confers nothing and a department never reads another's data, so
 * what this reveals depends on which side the reader is on. Either side may
 * read what was asked and what came back, and the builder never reaches for
 * the body or the result at all: it says who asked whom, for what, where it
 * stands and what to do, from the title and the state alone. A reader on
 * neither side (a superadmin looking in) is told so, and is given even less.
 */

const DAY = 86_400_000;

/** "today", "yesterday", "6 days ago", "3 weeks ago": how long ago, for a person. */
export function agoWords(iso: string | null | undefined, now: Date = new Date()): string | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const days = Math.max(0, Math.floor((now.getTime() - t) / DAY));
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 14) return `${days} days ago`;
  if (days < 60) return `${Math.floor(days / 7)} weeks ago`;
  return `${Math.floor(days / 30)} months ago`;
}

/** Whole days since, for a rule that turns on how long something has waited. */
export function daysSince(iso: string | null | undefined, now: Date = new Date()): number {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? 0 : Math.max(0, Math.floor((now.getTime() - t) / DAY));
}

const short = (iso: string | null | undefined): string | undefined =>
  iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : undefined;

/** What a request takes, in words: a result alone, or the tools and permissions it names. */
export function needsWords(r: Pick<WireRequest, 'needs'>): string {
  const parts = [...r.needs.tools.map(toolName), ...r.needs.scopes.map(scopeWords)];
  return parts.length === 0 ? 'A result, not access' : parts.join(', ');
}

export type Side = 'receiver' | 'requester' | 'outsider';

export function sideOf(r: Pick<WireRequest, 'fromDepartment' | 'toDepartment'>, department: string): Side {
  // A department asking itself is not a thing the platform allows; the receiving side wins if it ever happens.
  if (r.toDepartment === department) return 'receiver';
  if (r.fromDepartment === department) return 'requester';
  return 'outsider';
}

const needsGrant = (r: WireRequest): boolean => r.refusalReason?.startsWith('needs_grant:') === true;

export function explainRequest(r: WireRequest, who: Who, department: string, now: Date = new Date()): ItemContext {
  const side = sideOf(r, department);
  const from = who.label(r.fromDepartment);
  const to = who.label(r.toDepartment);
  const path = `/requests/${r.id}`;
  const waited = daysSince(r.createdAt, now);
  const age = agoWords(r.createdAt, now) ?? 'recently';
  const live = r.state === 'open' || r.state === 'accepted';
  const suggestion = r.procedure === 'page_suggestion';
  const lead = who.lead(r.toDepartment);
  const iAmReceivingLead = lead?.name === 'You';

  const stateLine =
    r.state === 'open'
      ? 'It is being routed to an agent or a lead.'
      : r.state === 'accepted'
        ? r.assigneeAgent
          ? `${r.assigneeAgentName ?? `${to}’s agent`} has it.`
          : `${to}’s lead has it: no agent there holds what it takes, or the agent’s run could not finish it.`
        : r.state === 'refused'
          ? needsGrant(r)
            ? 'It was refused: it needs a permission only a person can grant.'
            : 'It was refused, with a reason.'
          : r.state === 'done'
            ? r.acknowledgedAt
              ? 'It was answered, and the asker has seen the answer.'
              : 'It was answered.'
            : 'It was withdrawn.';

  const owner: Party | null | undefined =
    r.state === 'open'
      ? null
      : r.state === 'accepted'
        ? r.assigneeAgent
          ? r.assigneeAgentName
            ? { name: r.assigneeAgentName, kind: 'agent' as const, role: `${to}’s agent` }
            : (() => {
                const agents = who.agentsOf(r.toDepartment);
                return agents.length === 1 ? agents[0]! : { name: `${to}’s agent`, kind: 'agent' as const };
              })()
          : lead
        : r.state === 'refused' && side === 'requester' && needsGrant(r)
          ? who.lead(r.fromDepartment)
          : undefined;

  // What it takes only matters while it can still be done, or when it was the reason for a refusal.
  const needsLine = needsWords(r);

  let need: string | undefined;
  let why: string | undefined;
  if (side === 'receiver') {
    if (live) need = 'An answer: a result sealed back, which is all they will ever see — or a refusal with a reason. A request confers nothing, so you answer with what your department already holds.';
    why = `${from} asked your department${r.assigneeAgent ? ', and it was routed to an agent' : r.state === 'accepted' ? ', and it was routed to the lead' : ''}.`;
  } else if (side === 'requester') {
    if (r.state === 'done' && !r.acknowledgedAt) need = 'Read the result and press Got it, so both departments hold the receipt.';
    else if (r.state === 'refused' && needsGrant(r)) need = 'A person has to decide whether to grant what it takes; your lead has a task naming whom to ask.';
    else if (r.state === 'refused') need = 'Read the reason, then ask again with the gap closed, or let it go.';
    why = `Your department asked ${to} for this, so you see where it stands.`;
  } else {
    why = 'You are looking in from outside: neither department is yours.';
  }

  const base: Pick<ItemContext, 'subject' | 'summary' | 'need' | 'why' | 'open'> = {
    subject: { kind: 'request', title: r.title },
    summary: `${from} asked ${to} for “${r.title}”. ${stateLine}`,
    ...(need ? { need } : {}),
    ...(why ? { why } : {}),
    open: { to: path, label: 'Open the request' },
  };

  // A reader on neither side gets the shape of it and nothing a side would hold back (§18).
  if (side === 'outsider') {
    const decide = suggestion && r.state === 'done' && !r.suggestionDecision && who.superadmin;
    return {
      ...base,
      source: { label: `A request from ${from}`, when: short(r.createdAt) },
      broughtBy: null,
      owner,
      next: decide
        ? { tip: `${to} wrote this suggestion up and it waits for a decision. Read the spec on its page and accept or decline it; accepting opens an issue, and nothing is built by that.`, actions: [{ label: 'Read and decide', to: path, primary: true }] }
        : { tip: 'Nothing is needed from you. You are not on either side, so what was asked and what came back stay between the two departments.', instead: 'If you need to know more, ask the department that holds it.' },
      knows: who.knowers(r.toDepartment),
      facts: [['State', stateLine], ['Opened', short(r.createdAt) ?? null]],
    };
  }

  const asker = who.actor(r.requestedBy);
  // `onBehalfOf` is a person's id: read by the name the server gives, never
  // by taking the id apart as if it were an address.
  const behalf: Party | null = r.onBehalfOfName ? { name: r.onBehalfOfName === who.selfName ? 'You' : r.onBehalfOfName, kind: 'person' } : null;
  const sourceLabel = suggestion
    ? `A suggestion for a page, from ${from}`
    : r.procedure === 'research_market'
      ? `A research request from ${from}`
      : r.procedure === 'research_suppliers'
        ? `A search for manufacturers, asked by ${from}`
        : r.procedure === 'research_distributors'
          ? `A search for distributors, asked by ${from}`
        : r.projectId
          ? `A request from ${from}, raised inside a project`
          : `A request from ${from}`;

  const facts: ItemContext['facts'] = [
    ['From', from],
    ['To', to],
    ['It takes', needsLine],
    ['Opened', `${short(r.createdAt) ?? '—'} (${age})`],
    ['Routed to', r.state === 'open' ? 'Not yet' : r.assigneeAgent ? (r.assigneeAgentName ?? 'An agent') : r.state === 'refused' || r.state === 'cancelled' ? null : 'The lead'],
    ['Decided', short(r.decidedAt) ?? null],
    ['Answered', short(r.completedAt) ?? null],
    ['Seen by the asker', short(r.acknowledgedAt) ?? null],
    ['On behalf of', behalf && behalf.name !== asker?.name ? behalf.name : null],
  ];

  const knows = side === 'receiver' ? who.knowers(r.fromDepartment) : who.knowers(r.toDepartment);

  // The best next step, a rule over the state and the side.
  let next: ItemContext['next'];
  let task: ItemContext['task'];
  if (side === 'receiver') {
    if (live) {
      const slow = waited >= 3;
      if (r.state === 'open') {
        next = { tip: 'It is still being routed, which takes moments. Check back shortly; nothing is needed from you yet.', instead: 'If it stays here, answer or refuse it from the request’s page.', actions: [{ label: 'Answer or say no', to: path }] };
      } else if (r.assigneeAgent) {
        next = {
          tip: slow
            ? `${from} has waited ${waited} days and the agent has not closed it. Either seal the result back yourself or refuse with a reason, from the request’s page.`
            : 'The agent has it. Nothing is needed from you unless it stalls.',
          instead: slow ? 'Or leave it with the agent a little longer; the asker can withdraw it if they no longer need it.' : `If it has waited too long, seal the result back yourself, or refuse with a reason, from the request’s page.`,
          actions: [{ label: 'Answer or say no', to: path, primary: slow }],
        };
      } else if (iAmReceivingLead) {
        next = {
          tip: slow
            ? `It is yours, and ${from} has waited ${waited} days. Seal a result back from the request’s page — it is all they will see — or say no with a reason.`
            : 'It is yours: no agent in your department holds what it takes. Seal a result back — it is all they will see — or say no with a reason.',
          instead: 'If someone else in the department knows the answer, ask them and then seal it back yourself; there is no way to hand it on from here.',
          actions: [{ label: 'Answer or say no', to: path, primary: true }],
        };
      } else {
        next = {
          tip: `It is with ${lead?.name ?? `${to}’s lead`}, because no agent in your department holds what it takes. Nothing is needed from you unless you hold the answer.`,
          instead: 'If you do, tell them; only the department can seal the result back.',
        };
      }
      task = { title: `Answer ${from}: ${r.title}`, from: path };
    } else if (r.state === 'done') {
      next = {
        tip: r.acknowledgedAt ? 'Nothing to do. You answered and they have seen it.' : 'Nothing is needed from you. You sealed the result back; they will mark it seen.',
        instead: 'If they come back with more, it arrives as a new request.',
      };
    } else if (r.state === 'refused') {
      next = { tip: 'Nothing to do. It was refused, and the reason went to their lead as a task.', instead: 'If it comes back changed, it arrives as a new request.' };
    } else {
      next = { tip: 'Nothing to do. They withdrew it, and the task it made was cancelled with it.' };
    }
  } else if (suggestion && r.state === 'done' && !r.suggestionDecision && who.superadmin) {
    // The requester side of a suggestion is the one that decides it, and only a superadmin may.
    next = { tip: `${to} wrote the suggestion up and it waits for your decision. Read the spec and accept or decline it; accepting opens an issue, and nothing is built by that.`, actions: [{ label: 'Read and decide', to: path, primary: true }] };
  } else if (live) {
    const long = waited >= 5;
    next = {
      tip: long
        ? `You have waited ${waited} days and ${to} has not answered yet: ask ${lead?.name ?? `${to}’s lead`} directly, or withdraw it from the request’s page if you no longer need it.`
        : `Waiting on ${to}. Nothing is needed from you; the answer comes back here.`,
      instead: long ? 'Withdrawing cancels their task with it.' : 'If you no longer need it, withdraw it from the request’s page; their task is cancelled with it.',
      actions: long ? [{ label: 'Open to withdraw it', to: path }] : undefined,
    };
  } else if (r.state === 'done') {
    next = r.acknowledgedAt
      ? { tip: 'Nothing to do. It was answered and you have seen it.', instead: 'If you need more, ask again from Requests.' }
      : { tip: `${to} answered. Read the result and press Got it, so both departments hold the receipt.`, instead: 'It stays here either way; Got it only records that you saw it.', actions: [{ label: 'Read the result', to: path, primary: true }] };
    if (!r.acknowledgedAt) task = { title: `Read ${to}’s answer: ${r.title}`, from: path };
  } else if (r.state === 'refused') {
    next = needsGrant(r)
      ? {
          tip: `${to} cannot do this with what it has, and only a person can grant that. Your lead has a task naming whom to ask: start there.`,
          instead: 'Or ask again for the result alone, without naming what it takes, if the result is all you need.',
          actions: [{ label: 'Read why', to: path }],
        }
      : {
          tip: 'It was refused with a reason. Read it, then ask again with the gap closed, or let it go.',
          instead: 'Nothing happens if you leave it; it stays here as the record.',
          actions: [{ label: 'Read the reason', to: path, primary: true }],
        };
  } else {
    next = { tip: 'Nothing to do. You withdrew it, and the task it made was cancelled with it.', instead: 'If it comes back, ask again from Requests.' };
  }

  return {
    ...base,
    source: { label: sourceLabel, to: path, when: short(r.createdAt) },
    broughtBy: asker ? { ...asker, ...(short(r.createdAt) ? { when: short(r.createdAt)! } : {}) } : null,
    owner,
    knows,
    facts,
    next,
    ...(task ? { task } : {}),
  };
}

/**
 * One line of the grouped receipts: a department did something a number of
 * times and it ended some way. It is a count, not a record, so the panel
 * says what it counts, who is accountable for it, and where to read the
 * individual cases.
 */
export function explainActivity(r: ActivityRow, who: Who, days: number): ItemContext {
  const dept = who.label(r.department);
  const times = Number(r.occurrences);
  const what = actionWords(r.action);
  const outcome = outcomeWords(r);
  const bad = outcome.tone === 'danger';
  const withWords = r.counterparts.map((c) => who.label(c)).join(', ');
  // Who did them, as the reader may see them (0166); absent from a server
  // that does not say, and then said to be unrecorded rather than guessed.
  const doers = r.actors?.map((a) => who.actor(a)).filter((p): p is Party => p !== null);
  const doneBy = doers === undefined ? null : doers.map((p) => p.name).join(', ') || null;
  return {
    subject: { kind: 'receipts', title: `${dept}: ${what}` },
    summary: `${dept} did “${what}” ${times === 1 ? 'once' : `${times} times`} in the last ${days} days, and it ended as ${outcome.label.toLowerCase()}.${withWords ? ` It involved ${withWords}.` : ''}`,
    need: bad ? 'A look at why it did not go through, if it was not expected.' : undefined,
    why: 'Leads read their own department’s receipts, and a superadmin reads every department’s.',
    source: { label: 'Receipts from the platform’s audit log, grouped by department, action and outcome' },
    ...(doers === undefined ? { broughtBy: null } : {}),
    owner: who.lead(r.department),
    knows: who.knowers(r.department),
    facts: [
      ['Times', String(times)],
      ['Done by', doers === undefined ? null : doneBy && r.actors!.length >= 8 ? `${doneBy} (the eight who did it most)` : doneBy],
      ['Outcome', outcome.label.charAt(0) + outcome.label.slice(1).toLowerCase()],
      ['With', withWords || null],
    ],
    next: bad
      ? {
          tip: `${times === 1 ? 'It was' : 'These were'} ${outcome.label === 'REFUSED' ? 'refused by the platform, which is it working as it should' : 'failed'}. If you did not expect that, ask ${dept}’s lead what was attempted.`,
          instead: withWords ? 'The requests between these departments are on the Requests page, where each one says why.' : 'Nothing happens if you leave it; it is a record, not a task.',
          ...(withWords ? { actions: [{ label: 'See the requests', to: '/requests' }] } : {}),
        }
      : {
          tip: 'Nothing is needed from you. This is a count of what was done and recorded, for looking back.',
          instead: withWords ? 'To see what was asked between these departments, open the Requests page.' : undefined,
          ...(withWords ? { actions: [{ label: 'See the requests', to: '/requests' }] } : {}),
        },
  };
}

/**
 * The same context, for the record's own page: a link to the page you are on
 * says nothing, and the controls it points at are already on the page.
 */
export function onItsPage(c: ItemContext, path: string): ItemContext {
  const rest = { ...c };
  delete rest.open;
  const actions = c.next.actions?.filter((a) => a.to !== path);
  return { ...rest, next: { ...c.next, actions: actions && actions.length > 0 ? actions : undefined } };
}
